import React, { useEffect, useState } from "react"
import * as Yup from "yup"
import { useFormik } from "formik"
import withRouter from "components/Common/withRouter"
import PropTypes from "prop-types"
import {
  Card,
  CardBody,
  Form,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import Spinners from "components/Common/Spinner"
import {
  addNewGoodServicesHeadMaster,
  getGoodServicesHeadMasterDetail,
  updateGoodServicesHeadMaster,
  deleteGoodServicesHeadMaster,
  getGoodServicesHeadMasterList,
} from "store/actions"
import { getUsersAndGoodsServices } from "helpers/fakebackend_helper"

const NewGoodServicesHeadMaster = props => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Selectors
  const headsState = state => state.goodServicesHeadMaster.goodServicesHead.data
  const goodsServicesState = state =>
    state.goodServicesHeadMaster.goodServicesHead.data
  const detailState = state =>
    state.goodServicesHeadMaster.goodServicesHeadDetail.data

  const loadingState = state => state.goodServicesHeadMaster.loading

  const componentProperties = createSelector(
    [headsState, goodsServicesState, detailState, loadingState],
    (heads, goodsServices, detail, loading) => ({
      heads,
      goodsServices,
      detail,
      loading,
    })
  )

  const { heads, goodsServices, detail, loading } =
    useSelector(componentProperties)

    console.log("heads", detail)
  const [isEditMode, setIsEditMode] = useState(false)
  const [recordId, setRecordId] = useState(null)
  const [isLoading, setLoading] = useState(loading)

  useEffect(() => {
    // Fetch initial data
    dispatch(getGoodServicesHeadMasterList())

    // Check for edit mode
    if (location.pathname.includes("edit")) {
      const id = location.pathname.split("/edit-goods-services-head-master/")[1]
      setRecordId(id)
      setIsEditMode(true)
      dispatch(getGoodServicesHeadMasterDetail(id))
    }
  }, [dispatch])

  const [users, setUsers] = useState([]);
  const [serviceGoodsCategories, setServiceGoodsCategories] = useState([]);
  
  useEffect(() => {
    // Define and execute data fetching functions
    const fetchUserData = async () => {
      try {
        const response = await getUsersAndGoodsServices();
        setUsers(response.data.users);
        setServiceGoodsCategories(response.data.proposal_types);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
  
    // Call both fetch functions
    fetchUserData();
  }, []); // Empty dependency array means this runs once on mount

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      head_text: (detail && detail.head_text) || "",
      good_service_id: (detail && detail.good_service_id) || "",
      is_active: Boolean(detail && detail.is_active) || false,
    },
    validationSchema: Yup.object({
      head_text: Yup.string().required("Please select a Head"),
      good_service_id: Yup.number().required("Please select Goods/Services"),
      is_active: Yup.boolean(),
    }),
    onSubmit: values => {
      if (isEditMode) {
        dispatch(updateGoodServicesHeadMaster(recordId, values, navigate))
      } else {
        dispatch(addNewGoodServicesHeadMaster(values, navigate))
      }
    },
  })

  return (
    <div className="page-content">
      {isLoading ? (
        <Spinners setLoading={setLoading} />
      ) : (
        <>
          <Row>
            <Col>
              <div className="d-flex gap-2 mb-3">
                <i
                  className="mdi mdi-arrow-left font-size-20"
                  style={{ color: "grey", marginRight: "15px" }}
                  onClick={() => navigate(-1)}
                />
                <span
                  style={{
                    color: "black",
                    fontWeight: "500",
                    fontSize: "18px",
                  }}
                >
                  {isEditMode
                    ? "Edit Goods/Services Head"
                    : "New Goods/Services Head"}
                </span>
              </div>
            </Col>
            <Col>
              <Breadcrumbs title="Settings / Master configuration / Goods & Services Head" />
            </Col>
          </Row>

          <Card>
            <CardBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Head *</Label>
                      <Input
                        type="text"
                        name="head_text"
                        className="form-control"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.head_text}
                        invalid={
                          validation.touched.head_text &&
                          !!validation.errors.head_text
                        }
                      />
                      {validation.touched.head_text &&
                        validation.errors.head_text && (
                          <FormFeedback>
                            {validation.errors.head_text}
                          </FormFeedback>
                        )}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Goods/Services *</Label>
                      <Input
                        type="select"
                        name="good_service_id"
                        className="form-control"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.good_service_id}
                        invalid={
                          validation.touched.good_service_id &&
                          !!validation.errors.good_service_id
                        }
                      >
                        <option value="">Select Goods/Services</option>
                        {serviceGoodsCategories.map(item => (
                          <option key={item.id} value={item.id}>
                            {item.proposal_type}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.good_service_id &&
                        validation.errors.good_service_id && (
                          <FormFeedback>
                            {validation.errors.good_service_id}
                          </FormFeedback>
                        )}
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="mb-3">
                      <div className="form-check">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="is_active"
                          name="is_active"
                          checked={validation.values.is_active}
                          onChange={e =>
                            validation.setFieldValue(
                              "is_active",
                              e.target.checked
                            )
                          }
                        />
                        <Label className="form-check-label" htmlFor="is_active">
                          Is Active
                        </Label>
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="text-end">
                  <button type="submit" className="btn btn-primary w-md">
                    {isEditMode ? "Update" : "Submit"}
                  </button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  )
}

NewGoodServicesHeadMaster.propTypes = {
  router: PropTypes.object,
}

export default withRouter(NewGoodServicesHeadMaster)
